/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { of, throwError } from "rxjs";
import { HttpRequestOptions } from "../../../../models/ui/http-request-options.model";
import {
	GET_ALL_ANOMALOUS_APPLICATIONS_RESPONSE,
	GET_ANOMALIES_BY_APPLICATION_RESPONSE
} from "./anomaly-detection.db";
import { ApplicationError } from "../../../../models/ui/application-error.model";

const CLASS_NAME = "AnomalyDetection";

export class AnomalyDetection {
	public static getAllAnomalousApplications(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						CLASS_NAME,
						"getAllAnomalousApplications"
					)
			);

		const tenantId = pathParams?.["tenantId"];

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing tenant token parameter",
						CLASS_NAME,
						"getAllAnomalousApplications"
					)
			);

		const page = httpParams.get("page");
		const size = httpParams.get("size");
		const startDate = httpParams.get("startDate");

		if (!page || !size || !startDate)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						CLASS_NAME,
						"getAllAnomalousApplications"
					)
			);

		const pages = GET_ALL_ANOMALOUS_APPLICATIONS_RESPONSE;
		const position = +page > pages.length ? pages.length - 1 : +page;

		return of(pages[position]);
	}

	public static getAnomaliesByApplication(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						CLASS_NAME,
						"getAnomaliesByApplication"
					)
			);

		const startDate = httpParams.get("startDate");
		const tenantId = httpParams.get("tenantId");
		const page = httpParams.get("page");
		const size = httpParams.get("size");

		if (!startDate || !tenantId || !page || !size)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						CLASS_NAME,
						"getAnomaliesByApplication"
					)
			);

		const applicationId = pathParams?.["applicationId"];

		if (!applicationId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required path parameter",
						CLASS_NAME,
						"getAnomaliesByApplication"
					)
			);

		const pages = GET_ANOMALIES_BY_APPLICATION_RESPONSE.pages;
		const position = +page > pages.length ? pages.length - 1 : +page;
		const selectedPage = pages[position];

		const { sciComponentELineChart, co2LineChart, sciLineChart } = {
			...GET_ANOMALIES_BY_APPLICATION_RESPONSE
		};

		const response = {
			pagedTable: selectedPage,
			sciComponentELineChart,
			co2LineChart,
			sciLineChart
		};

		return of(response);
	}

	public static addUserDeviceByTenant(options: HttpRequestOptions) {
		const tenantId = options.pathParams?.["tenantId"];

		if (!tenantId || !options.body) {
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						CLASS_NAME,
						"addUserDeviceByTenant"
					)
			);
		}

		return of("Device Added");
	}

	public static updateUserNotificationSettings(options: HttpRequestOptions) {
		const httpParams = options.params as HttpParams;
		const tenantId = options.pathParams?.["tenantId"];

		if (!tenantId || !options.body || httpParams?.get("activeDark") === null) {
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						CLASS_NAME,
						"updateUserNotificationSettings"
					)
			);
		}

		return of("Notification On/Off");
	}
}
